"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
const RESOURCE_TIMING = 'ResourceTiming';
const NAVIGATION_TIMING = 'NavigationTiming';
const PAINT_TIMING = 'PaintTiming';
/**
 * Send a PerformanceEntry object to Metrics storage.
 *
 * @param {object} entry A PerformanceEntry object.
 * @param {string} type The type of Metrics record to create.
 * @param {string} event An optional event.
 */
function sendEntry(entry, type, event) {
    const m = {
        data: entry,
        dateNow: Date.now(),
        event,
        name: 'exc.metrics-browser-runtime.performance',
        recordType: type
    };
    global.adobeMetrics.queue.push(m);
}
/**
 * Send a PerformanceResourceTiming object to Metrics storage.
 * To avoid looping, the resourceTiming object will be ignored
 * if it is an interaction with the telemetry gateway.
 *
 * @param {object} resourceTiming A resourceTiming object.
 * @param {string} event An optional event.
 */
function sendResourceTiming(resourceTiming, event) {
    // skip telemetry/metrics POST to avoid looping
    // this bit of defensive coding is because resourceTiming.name.startsWith
    // can be undefined in some browsers, depending on who the
    // underlying Performance object is implemented
    if (resourceTiming.name && resourceTiming.name.substr(0, 17) !== 'https://telemetry') {
        sendEntry(resourceTiming, RESOURCE_TIMING, event);
    }
}
/**
 * A callback to record ResourceTiming events from
 * a PerformanceObserver instance.
 *
 * @param {object} list The PerformanceObserverEntryList object.
 */
function recordResource(list) {
    const entries = list.getEntries();
    for (let i = 0; i < entries.length; i++) {
        sendResourceTiming(entries[i]);
    }
}
/**
 * A callback to record PaintTiming events from
 * a PerformanceObserver instance.
 *
 * @param {object} list The PerformanceObserverEntryList object.
 */
function recordPaint(list) {
    const entries = list.getEntries();
    // using a for{} loop because sometimes entries.call()
    // is null, causing forEach() to fail
    for (let i = 0; i < entries.length; i++) {
        sendEntry(entries[i], PAINT_TIMING);
    }
}
/**
 * A callback to record NavigationTiming events from
 * a PerformanceObserver instance.
 *
 * @param {object} list The PerformanceObserverEntryList object.
 */
function recordNavigation(list) {
    const entries = list.getEntries();
    // using a for{} loop because sometimes entries.call()
    // is null, causing forEach() to fail
    for (let i = 0; i < entries.length; i++) {
        sendEntry(entries[i], NAVIGATION_TIMING);
    }
}
/**
 * Initialize the window performance object exactly one time.
 */
function initPerformance() {
    const a = global.adobeMetrics;
    const p = window.performance;
    let resourceObserver;
    let paintObserver;
    let navigationObserver;
    if (a.init.performance) {
        return;
    }
    a.init.performance = {};
    if (p && p.getEntriesByType) {
        let entries = p.getEntriesByType('resource');
        for (let i = 0; i < entries.length; i++) {
            sendResourceTiming(entries[i], 'init');
        }
        entries = p.getEntriesByType('paint');
        for (let i = 0; i < entries.length; i++) {
            sendEntry(entries[i], PAINT_TIMING, 'init');
        }
        entries = p.getEntriesByType('navigation');
        for (let i = 0; i < entries.length; i++) {
            sendEntry(entries[i], NAVIGATION_TIMING, 'init');
        }
    }
    // Using (global as any) because Typescript doesn't
    // define Window.PerfomanceObserver
    if (global['PerformanceObserver']) {
        try {
            resourceObserver = new PerformanceObserver(recordResource);
            resourceObserver.observe({ entryTypes: ['resource'] });
        }
        catch (e) {
            // not all browsers support all types - ignore
            resourceObserver && resourceObserver.disconnect();
            resourceObserver = undefined;
        }
        try {
            paintObserver = new PerformanceObserver(recordPaint);
            paintObserver.observe({ entryTypes: ['paint'] });
        }
        catch (e) {
            // not all browsers support all types - ignore
            paintObserver && paintObserver.disconnect();
            paintObserver = undefined;
        }
        try {
            navigationObserver = new PerformanceObserver(recordNavigation);
            navigationObserver.observe({ entryTypes: ['navigation'] });
        }
        catch (e) {
            // not all browsers support all types - ignore
            navigationObserver && navigationObserver.disconnect();
            navigationObserver = undefined;
        }
        a.init.performance = {
            navigationObserver,
            paintObserver,
            resourceObserver
        };
    }
}
exports.default = initPerformance;
//# sourceMappingURL=Performance.js.map